/**
 * ProtectedRoute Component
 * Reason: Validate JWT token and redirect to login if invalid or expired
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';

const ProtectedRoute = ({ children }) => {
  const { token, logout } = useAuthStore();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = () => {
    // No token - redirect to login
    if (!token) {
      setIsValidating(false);
      setIsValid(false);
      return;
    }

    try {
      // Decode JWT token (without verification, just to check expiration)
      // Reason: JWT format is header.payload.signature, we only need payload
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Check if token is expired
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      if (currentTime >= expirationTime) {
        // Token expired
        console.warn('Token expired, logging out...');
        toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        logout();
        setIsValid(false);
      } else {
        // Token is valid
        setIsValid(true);
      }
    } catch (error) {
      // Invalid token format
      console.error('Invalid token format:', error);
      toast.error('Token inválido. Por favor, inicia sesión nuevamente.');
      logout();
      setIsValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center app-gradient">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-300">Validando sesión...</p>
        </div>
      </div>
    );
  }

  // If token is invalid or expired, redirect to login
  if (!isValid) {
    return <Navigate to="/auth" replace />;
  }

  // Token is valid, render the protected component
  return children;
};

export default ProtectedRoute;

